import { useState, useRef } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  Badge,
  Progress,
  Spinner,
  Divider,
  IconButton,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { AddIcon, DeleteIcon, CheckIcon } from '@chakra-ui/icons'
import { glassCard, inputStyle, labelStyle } from '../styles'
import { apiFetch } from '../utils/apiFetch'
import { usePollContext, Poll } from '../contexts/PollContext'

export default function VoteTap() {
  const [user] = useState(() => {
    const data = localStorage.getItem('user')
    return data ? JSON.parse(data) : null
  })

  const { polls, loading, refresh, updatePoll, prependPoll } = usePollContext()

  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ title: '', description: '', options: ['', ''] })
  const [creating, setSaving] = useState(false)
  const [dismissingAll, setDismissingAll] = useState(false)
  const [voting, setVoting] = useState<Record<string, boolean>>({})
  const [closing, setClosing] = useState<Record<string, boolean>>({})
  const [pollToClose, setPollToClose] = useState<string | null>(null)
  const { isOpen: isCloseConfirmOpen, onOpen: onCloseConfirmOpen, onClose: onCloseConfirmClose } = useDisclosure()
  const cancelCloseRef = useRef<HTMLButtonElement>(null)

  const pendingPolls = polls.filter(poll => !poll.userResponse && !poll.closedAt)

  const handleVote = async (pollId: string, optionId: string) => {
    setVoting(prev => ({ ...prev, [pollId]: true }))
    const res = await apiFetch(`/api/polls/${pollId}/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ optionId }),
    })
    if (res.ok) {
      const updated: Poll = await res.json()
      updatePoll(updated)
    }
    setVoting(prev => ({ ...prev, [pollId]: false }))
  }

  const handleDismiss = async (pollId: string) => {
    setVoting(prev => ({ ...prev, [pollId]: true }))
    const res = await apiFetch(`/api/polls/${pollId}/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dismiss: true }),
    })
    if (res.ok) {
      const updated: Poll = await res.json()
      updatePoll(updated)
    }
    setVoting(prev => ({ ...prev, [pollId]: false }))
  }

  const handleDismissAll = async () => {
    setDismissingAll(true)
    const res = await apiFetch('/api/polls/dismiss-all', { method: 'POST' })
    if (res.ok) refresh()
    setDismissingAll(false)
  }

  const handleClose = async (pollId: string) => {
    setClosing(prev => ({ ...prev, [pollId]: true }))
    const res = await apiFetch(`/api/polls/${pollId}/close`, { method: 'PATCH' })
    if (res.ok) refresh()
    setClosing(prev => ({ ...prev, [pollId]: false }))
  }

  const handleCreate = async () => {
    const options = form.options.map(option => option.trim()).filter(Boolean)
    if (!form.title.trim() || options.length < 2) return
    setSaving(true)
    const res = await apiFetch('/api/polls', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        options,
      }),
    })
    if (res.ok) {
      const created: Poll = await res.json()
      prependPoll(created)
      setForm({ title: '', description: '', options: ['', ''] })
      setShowCreate(false)
    }
    setSaving(false)
  }

  const addOption = () => {
    if (form.options.length < 6) setForm(prev => ({ ...prev, options: [...prev.options, ''] }))
  }
  const removeOption = (index: number) => {
    if (form.options.length > 2) setForm(prev => ({ ...prev, options: prev.options.filter((_optionText, itemIndex) => itemIndex !== index) }))
  }
  const updateOption = (index: number, value: string) =>
    setForm(prev => ({ ...prev, options: prev.options.map((optionText, itemIndex) => itemIndex === index ? value : optionText) }))

  return (
    <Box minH="100vh" position="relative" p={{ base: 4, md: 8 }} pt={{ base: 16, md: 20 }}>

      <Box maxW="700px" mx="auto">
        <VStack spacing={6} align="stretch">

          <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
            <Text fontSize="2xl" fontWeight="bold" color="white">🙋 The Vote Tap</Text>
            <HStack spacing={2}>
              {pendingPolls.length > 0 && (
                <Button size="sm" variant="ghost" color="whiteAlpha.600" isLoading={dismissingAll} onClick={handleDismissAll}>
                  Dismiss all pending
                </Button>
              )}
              <Button size="sm" colorScheme="orange" variant={showCreate ? 'solid' : 'outline'} onClick={() => setShowCreate(current => !current)}>
                {showCreate ? 'Cancel' : 'Create poll'}
              </Button>
            </HStack>
          </HStack>

          {showCreate && (
            <Box {...glassCard} borderRadius="xl" p={{ base: 4, md: 6 }}>
              <VStack spacing={4} align="stretch">
                <Text {...labelStyle}>New Poll</Text>

                <Input
                  placeholder="Question / title"
                  value={form.title}
                  onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                  {...inputStyle}
                />
                <Textarea
                  placeholder="Description (optional)"
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  {...inputStyle}
                  rows={2}
                />

                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text {...labelStyle}>Options</Text>
                    {form.options.length < 6 && (
                      <Button size="xs" variant="ghost" color="orange.300" leftIcon={<AddIcon />} onClick={addOption}>Add</Button>
                    )}
                  </HStack>
                  {form.options.map((optionText, index) => (
                    <HStack key={index} spacing={2}>
                      <Input
                        placeholder={`Option ${index + 1}`}
                        value={optionText}
                        onChange={e => updateOption(index, e.target.value)}
                        {...inputStyle}
                      />
                      {form.options.length > 2 && (
                        <IconButton aria-label="Remove" icon={<DeleteIcon />} size="xs" variant="ghost" color="red.300" onClick={() => removeOption(index)} />
                      )}
                    </HStack>
                  ))}
                </VStack>

                <HStack justify="flex-end">
                  <Button size="sm" colorScheme="orange" isLoading={creating} onClick={handleCreate}>
                    Create
                  </Button>
                </HStack>
              </VStack>
            </Box>
          )}

          {loading ? (
            <Spinner color="white" />
          ) : polls.length === 0 ? (
            <Text color="whiteAlpha.500" fontStyle="italic">No polls yet. Be the first to create one!</Text>
          ) : (
            <VStack spacing={4} align="stretch">
              {polls.map(poll => {
                const isPending = !poll.userResponse && !poll.closedAt
                const isVoting = voting[poll._id]
                const isClosed = !!poll.closedAt
                const votedOptionId = poll.userResponse?.optionId
                const dismissed = poll.userResponse?.dismissed

                return (
                  <Box key={poll._id} {...glassCard} borderRadius="xl" p={{ base: 4, md: 6 }}
                    borderColor={isPending ? 'orange.400' : 'whiteAlpha.200'}
                  >
                    <VStack spacing={3} align="stretch">

                      <HStack justify="space-between" align="start">
                        <VStack align="start" spacing={0} flex={1}>
                          <HStack spacing={2} flexWrap="wrap">
                            <Text color="white" fontWeight="semibold">{poll.title}</Text>
                            {isClosed && <Badge colorScheme="gray" fontSize="xs">Closed</Badge>}
                            {dismissed && <Badge colorScheme="gray" variant="subtle" fontSize="xs">Dismissed</Badge>}
                            {isPending && <Badge colorScheme="orange" fontSize="xs">Vote pending</Badge>}
                          </HStack>
                          {poll.description && (
                            <Text color="whiteAlpha.600" fontSize="sm">{poll.description}</Text>
                          )}
                        </VStack>
                        {(poll.createdByMe || user?.isAdmin) && !isClosed && (
                          <Button size="xs" variant="ghost" color="whiteAlpha.500" isLoading={closing[poll._id]}
                            onClick={() => { setPollToClose(poll._id); onCloseConfirmOpen() }}>
                            Close
                          </Button>
                        )}
                      </HStack>

                      <Divider borderColor="whiteAlpha.100" />

                      {isPending ? (
                        <VStack spacing={2} align="stretch">
                          {poll.options.map(option => (
                            <Button
                              key={option._id}
                              variant="ghost"
                              justifyContent="flex-start"
                              color="whiteAlpha.800"
                              borderColor="whiteAlpha.200"
                              border="1px solid"
                              borderRadius="md"
                              size="sm"
                              isLoading={isVoting}
                              onClick={() => handleVote(poll._id, option._id)}
                              _hover={{ bg: 'whiteAlpha.100', color: 'white', borderColor: 'orange.300' }}
                            >
                              {option.text}
                            </Button>
                          ))}
                          <HStack justify="flex-end">
                            <Button size="xs" variant="ghost" color="whiteAlpha.400" isLoading={isVoting}
                              onClick={() => handleDismiss(poll._id)}>
                              Dismiss
                            </Button>
                          </HStack>
                        </VStack>
                      ) : (
                        <VStack spacing={3} align="stretch">
                          {poll.options.map(option => {
                            const result = poll.results?.find(pollResult => pollResult.optionId === option._id)
                            const count = result?.count ?? 0
                            const total = result?.total ?? 0
                            const pct = total > 0 ? Math.round((count / total) * 100) : 0
                            const isMyVote = votedOptionId === option._id

                            return (
                              <VStack key={option._id} spacing={1} align="stretch">
                                <HStack justify="space-between">
                                  <HStack spacing={1}>
                                    {isMyVote && <CheckIcon color="orange.300" boxSize={3} />}
                                    <Text fontSize="sm" color={isMyVote ? 'orange.300' : 'whiteAlpha.800'} fontWeight={isMyVote ? 'semibold' : 'normal'}>
                                      {option.text}
                                    </Text>
                                  </HStack>
                                  <Text fontSize="xs" color="whiteAlpha.500">{count} ({pct}%)</Text>
                                </HStack>
                                <Progress value={pct} colorScheme="orange" size="sm" borderRadius="full" bg="whiteAlpha.100" />
                              </VStack>
                            )
                          })}
                          {poll.results && (
                            <Text fontSize="xs" color="whiteAlpha.400" textAlign="right">
                              {poll.results[0]?.total ?? 0} vote{(poll.results[0]?.total ?? 0) !== 1 ? 's' : ''} total
                            </Text>
                          )}
                        </VStack>
                      )}

                    </VStack>
                  </Box>
                )
              })}
            </VStack>
          )}
        </VStack>
      </Box>

      <AlertDialog isOpen={isCloseConfirmOpen} leastDestructiveRef={cancelCloseRef} onClose={onCloseConfirmClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" border="1px solid" borderColor="whiteAlpha.200">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="white">Close Poll</AlertDialogHeader>
            <AlertDialogBody color="whiteAlpha.800">
              Close this poll? Results will be locked and no more votes can be cast.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelCloseRef} variant="ghost" color="whiteAlpha.700" onClick={onCloseConfirmClose}>Cancel</Button>
              <Button colorScheme="orange" ml={3} onClick={() => {
                if (pollToClose) handleClose(pollToClose)
                onCloseConfirmClose()
              }}>
                Close Poll
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}
